import { useEffect, useMemo, useRef, useState } from "react";
import * as S from "./styles";
import WaybeLogo from "../../assets/images/waybe_logo.png";
import { Close, ArrowBack, ArrowForward } from "@mui/icons-material";
import { useTheme } from "@emotion/react";
import useTamanhoTela from "../../utils/useTamanhoTela";
import { BotaoAzulVazado } from "../Botao";

export default function ModalInformativo({
  image,
  hiperlink,
  registrarClick = () => {},
  naoExibirNovamente = () => {},
  descritivoModal = "",
  tamanho = "md",
  close,
  totalNotificacoes = 1,
  indexNotificacao = 0,
  onNavigate,
}) {
  const theme = useTheme();
  const { largura } = useTamanhoTela();
  const [urlTemporaria, setUrlTemporaria] = useState(null);
  const bannerImageRef = useRef(null);
  const previousArrowRef = useRef(null);
  const tamanhoDesktop = largura > 1100 ? tamanho : "md";
  const isLarge = tamanhoDesktop === "lg";
  const srcImagem = useMemo(() => {
    if (!image) {
      return null;
    }

    if (typeof image === "string") {
      return image;
    }

    if (typeof image?.preview === "string" && image.preview.length > 0) {
      return image.preview;
    }

    if (image instanceof File) {
      return null;
    }

    return null;
  }, [image]);

  useEffect(() => {
    if (image instanceof File && !image.preview) {
      const objectUrl = URL.createObjectURL(image);
      setUrlTemporaria(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
        setUrlTemporaria(null);
      };
    }

    setUrlTemporaria(null);

    return undefined;
  }, [image]);

  useEffect(() => {
    if (
      totalNotificacoes > 1 &&
      previousArrowRef.current &&
      document.activeElement === previousArrowRef.current
    ) {
      previousArrowRef.current.blur();
    }
  }, [totalNotificacoes]);

  const imagemRender = srcImagem || urlTemporaria;
  const isDesktop = largura > 1100;

  useEffect(() => {
    if (!imagemRender) return;

    const idFrame = requestAnimationFrame(() => {
      if (bannerImageRef.current instanceof HTMLElement) {
        bannerImageRef.current.focus();
      }
    });

    return () => {
      cancelAnimationFrame(idFrame);
    };
  }, [imagemRender, indexNotificacao]);

  const dimensoes = useMemo(() => {
    switch (tamanhoDesktop) {
      case "sm":
        return { width: "600px", maxHeight: "525px" };
      case "md":
        return { width: "800px", maxHeight: "525px" };
      case "lg":
        return { width: "1200px", minHeight: "425px" };
      default:
        return { width: "800px", maxHeight: "525px" };
    }
  }, [tamanhoDesktop]);

  const handlePrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onNavigate) {
      const newIndex = indexNotificacao === 0 ? totalNotificacoes - 1 : indexNotificacao - 1;
      onNavigate(newIndex);
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onNavigate) {
      const newIndex = indexNotificacao === totalNotificacoes - 1 ? 0 : indexNotificacao + 1;
      onNavigate(newIndex);
    }
  };

  const handleDotClick = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onNavigate) {
      onNavigate(index);
    }
  };

  function handleClick(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (hiperlink) {
      registrarClick();
      const url =
        hiperlink.startsWith("http://") || hiperlink.startsWith("https://")
          ? hiperlink
          : `https://${hiperlink}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  function handleImageKeyDown(e) {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleClick(e);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
        position: "relative",
      }}
    >
      <Close
        style={{
          color: theme.cores.semanticDanger2,
          cursor: "pointer",
          fontSize: 32,
          position: "absolute",
          top: largura < 1100 ? -16 : -8,
          right: largura < 1100 ? -16 : -8,
          zIndex: 1000,
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          close?.();
        }}
      />

      <S.BannerContainer
        $isLarge={isLarge}
        href={hiperlink}
        style={{
          cursor: hiperlink ? "pointer" : "auto",
          ...(isDesktop && {
            width: `min(${dimensoes.width}, calc(100vw - 64px))`,
            maxHeight: dimensoes.maxHeight,
          }),
          minHeight: dimensoes.minHeight,
        }}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          handleClick(e);
        }}
      >
        {isLarge ? (
          <>
            <div
              style={{
                width: "50%",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imagemRender && (
                <S.BannerImage
                  ref={bannerImageRef}
                  tabIndex={-1}
                  $isLarge
                  src={imagemRender}
                  alt="Banner"
                  onKeyDown={handleImageKeyDown}
                />
              )}
            </div>

            <div
              style={{
                width: "50%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  minHeight: 72,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {descritivoModal && (
                  <p
                    style={{
                      margin: 0,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    {descritivoModal}
                  </p>
                )}
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <img src={WaybeLogo} alt="Waybe Logo" style={{ width: "100px", height: "24px" }} />
                <BotaoAzulVazado
                  texto={"Não exibir novamente"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    naoExibirNovamente();
                  }}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {imagemRender && (
              <S.BannerImage
                ref={bannerImageRef}
                tabIndex={-1}
                src={imagemRender}
                alt="Banner"
                onKeyDown={handleImageKeyDown}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 40,
                width: "100%",
              }}
            >
              <div
                style={{
                  minHeight: 72,
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                {descritivoModal && (
                  <p
                    style={{
                      margin: 0,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      textAlign: "left",
                      width: "100%",
                    }}
                  >
                    {descritivoModal}
                  </p>
                )}
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <img src={WaybeLogo} alt="Waybe Logo" style={{ width: "100px", height: "24px" }} />
                <BotaoAzulVazado
                  texto={"Não exibir novamente"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    naoExibirNovamente();
                  }}
                />
              </div>
            </div>
          </>
        )}
      </S.BannerContainer>

      {totalNotificacoes > 1 && (
        <S.CarouselControls>
          <S.ArrowButton
            ref={previousArrowRef}
            onClick={handlePrevious}
            title="Notificação anterior"
          >
            <ArrowBack />
          </S.ArrowButton>

          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              flex: 1,
            }}
          >
            {Array.from({ length: totalNotificacoes }).map((_, index) => (
              <S.Dot
                key={index}
                $isActive={index === indexNotificacao}
                onClick={(e) => handleDotClick(index, e)}
                title={`Ir para notificação ${index + 1}`}
              />
            ))}
          </div>

          <S.ArrowButton onClick={handleNext} title="Próxima notificação">
            <ArrowForward />
          </S.ArrowButton>
        </S.CarouselControls>
      )}
    </div>
  );
}
